import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { inject, booleanAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, EventEmitter, Output, NgModule } from '@angular/core';
import { RouterLinkActive } from '@angular/router';
import * as i1 from '@odx/angular';
import { WithDisabledState, WithTabIndex, CoreModule } from '@odx/angular';
import { IconComponent } from '@odx/angular/components/icon';
import { CSSComponent, CSSModifier } from '@odx/angular/internal';
import { injectElement } from '@odx/angular/utils';

/**
 * RailNavigationItemComponent represents an individual navigation item within a rail navigation system.
 * This component can be used as either a link or a button and integrates with Angular's RouterLinkActive to
 * optionally highlight active navigation items.
 * Has host directives for disabled and tabindex states.
 *
 * @see {WithDisabledState}
 * @see {WithTabIndex}
 */
let RailNavigationItemComponent = class RailNavigationItemComponent {
    constructor() {
        this.routerLinkActive = inject(RouterLinkActive, { optional: true });
        this.element = injectElement();
        /**
         * Indicates whether the navigation item is selected. This can be manually set or derived from router state.
         * Uses booleanAttribute transformer for attribute binding.
         *
         * @type {boolean}
         * @default false
         */
        this.isSelected = false;
        /**
         * Optional input to specify the icon set from which the icon should be taken.
         *
         * @type {string}
         * @default 'core'
         */
        this.iconSet = 'core';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "18.2.13", type: RailNavigationItemComponent, isStandalone: true, selector: "a[odxRailNavigationItem][iconName], button[odxRailNavigationItem][iconName]", inputs: { isSelected: ["isSelected", "isSelected", booleanAttribute], iconName: "iconName", iconSet: "iconSet" }, host: { properties: { "class.is-selected": "isSelected || routerLinkActive?.isActive" } }, hostDirectives: [{ directive: i1.WithDisabledState }, { directive: i1.WithTabIndex }], ngImport: i0, template: "<odx-icon [name]=\"iconName\" [iconSet]=\"iconSet\" size=\"large\" />\n<span class=\"odx-rail-navigation-item__container\">\n  <ng-content />\n</span>\n", dependencies: [{ kind: "component", type: IconComponent, selector: "odx-icon", inputs: ["inline", "size", "name", "iconSet", "identifier"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
RailNavigationItemComponent = __decorate([
    CSSComponent('rail-navigation-item')
], RailNavigationItemComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationItemComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'a[odxRailNavigationItem][iconName], button[odxRailNavigationItem][iconName]', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, imports: [IconComponent], hostDirectives: [WithDisabledState, WithTabIndex], host: {
                        '[class.is-selected]': 'isSelected || routerLinkActive?.isActive',
                    }, template: "<odx-icon [name]=\"iconName\" [iconSet]=\"iconSet\" size=\"large\" />\n<span class=\"odx-rail-navigation-item__container\">\n  <ng-content />\n</span>\n" }]
        }], propDecorators: { isSelected: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], iconName: [{
                type: Input,
                args: [{ required: true }]
            }], iconSet: [{
                type: Input
            }] } });

const RailNavigationVariant = {
    DEFAULT: 'default',
    BAR: 'bar',
};

/**
 * RailNavigationComponent provides a navigational sidebar that can toggle between open and closed states.
 * It supports different visual variants and can integrate individual navigation items
 * for complex navigation structures.
 */
let RailNavigationComponent = class RailNavigationComponent {
    constructor() {
        this.element = injectElement();
        /**
         * Indicates whether the rail navigation is open. Uses booleanAttribute transformer for attribute binding.
         *
         * @type {boolean}
         * @default true
         */
        this.isOpen = true;
        /**
         * Specifies the visual variant of the rail navigation.
         *
         * @type {RailNavigationVariant}
         * @default RailNavigationVariant.DEFAULT
         */
        this.variant = RailNavigationVariant.DEFAULT;
        /**
         * Emits changes to the `isOpen` state, allowing for external reaction to state changes.
         *
         * @emits {boolean} - The new state of the rail navigation.
         */
        this.isOpenChange = new EventEmitter();
    }
    /**
     * Toggles the open state of the rail navigation. Optionally accepts a boolean to force the state to open or closed.
     * Does not perform any action if the variant is set to BAR.
     *
     * @param {boolean | undefined} forceState - Optionally force the toggle to a specific state.
     */
    toggle(forceState) {
        if (this.variant === RailNavigationVariant.BAR)
            return;
        this.isOpen = forceState ?? !this.isOpen;
        this.isOpenChange.emit(this.isOpen);
    }
    /**
     * Explicitly opens the rail navigation, setting `isOpen` to true.
     */
    open() {
        this.toggle(true);
    }
    /**
     * Explicitly closes the rail navigation, setting `isOpen` to false.
     */
    close() {
        this.toggle(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "18.2.13", type: RailNavigationComponent, isStandalone: true, selector: "odx-rail-navigation", inputs: { isOpen: ["isOpen", "isOpen", booleanAttribute], variant: "variant" }, outputs: { isOpenChange: "isOpenChange" }, host: { properties: { "class.is-open": "isOpen" } }, ngImport: i0, template: "<nav class=\"odx-rail-navigation__content\">\n  <ng-content selector=\"a[odxRailNavigationItem], button[odxRailNavigationItem]\" />\n</nav>\n<button odxRailNavigationItem class=\"odx-rail-navigation__expand\" iconName=\"chevron-right\" (click)=\"toggle()\"></button>\n", dependencies: [{ kind: "component", type: RailNavigationItemComponent, selector: "a[odxRailNavigationItem][iconName], button[odxRailNavigationItem][iconName]", inputs: ["isSelected", "iconName", "iconSet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", String)
], RailNavigationComponent.prototype, "variant", void 0);
RailNavigationComponent = __decorate([
    CSSComponent('rail-navigation')
], RailNavigationComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-rail-navigation', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, imports: [RailNavigationItemComponent], host: {
                        '[class.is-open]': 'isOpen',
                    }, template: "<nav class=\"odx-rail-navigation__content\">\n  <ng-content selector=\"a[odxRailNavigationItem], button[odxRailNavigationItem]\" />\n</nav>\n<button odxRailNavigationItem class=\"odx-rail-navigation__expand\" iconName=\"chevron-right\" (click)=\"toggle()\"></button>\n" }]
        }], propDecorators: { isOpen: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], variant: [{
                type: Input
            }], isOpenChange: [{
                type: Output
            }] } });

const modules = [RailNavigationComponent, RailNavigationItemComponent];
class RailNavigationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationModule, imports: [RailNavigationComponent, RailNavigationItemComponent], exports: [CoreModule, RailNavigationComponent, RailNavigationItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationModule, imports: [modules, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RailNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RailNavigationComponent, RailNavigationItemComponent, RailNavigationModule, RailNavigationVariant };
